'use client';

import React from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { useRef } from 'react';
import { Search, Stethoscope, Map, Rocket, BarChart3 } from 'lucide-react';
import { SECTION_HEADING_CLASS, SECTION_HEADING_STYLE } from './sectionHeading';

const steps = [
  {
    id: 'step-discover',
    number: '01',
    icon: Search,
    title: 'Discover',
    description:
      'We start by understanding your business, your market, your customers, and where the real growth opportunities lie. No assumptions — just structured discovery.',
    duration: '1–2 sessions',
  },
  {
    id: 'step-diagnose',
    number: '02',
    icon: Stethoscope,
    title: 'Diagnose',
    description:
      'We analyze your data, processes, and market position to identify the specific gaps and highest-impact levers. You get a clear diagnosis — not generic advice.',
    duration: '1 week',
  },
  {
    id: 'step-develop',
    number: '03',
    icon: Map,
    title: 'Develop the Strategy',
    description:
      'We build a practical, prioritized strategy tailored to your business stage, budget, and goals. Every recommendation is actionable, not aspirational.',
    duration: '1–2 weeks',
  },
  {
    id: 'step-execute',
    number: '04',
    icon: Rocket,
    title: 'Execute & Support',
    description:
      'We implement the strategy alongside your team — running campaigns, building assets, and providing hands-on support so nothing falls through the cracks.',
    duration: 'Ongoing',
  },
  {
    id: 'step-measure',
    number: '05',
    icon: BarChart3,
    title: 'Measure & Optimise',
    description:
      'We track what matters, report clearly, and continuously optimize based on real data. Every decision is informed by evidence, not gut feel.',
    duration: 'Monthly',
  },
];

export default function HowItWorksSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const prefersReducedMotion = useReducedMotion();

  return (
    <section
      data-ground
      id="how-it-works"
      className="section-rhythm"
      ref={ref}
      style={{
        background: 'var(--m-band)',
        borderTop: '1px solid var(--m-band-edge)',
        borderBottom: '1px solid var(--m-band-edge)',
      }}
    >
      <div className="m-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-8 sm:mb-16 lg:mb-20"
        >
          <div className="flex items-center gap-3 mb-5">
            <div style={{ width: 24, height: 1, background: 'rgba(var(--m-on-band-rgb),0.2)' }} />
            <span
              className="font-mono text-[11px] uppercase tracking-widest"
              style={{ color: 'rgba(var(--m-on-band-rgb),0.35)', letterSpacing: '0.22em' }}
            >
              Our Process
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2
              className={SECTION_HEADING_CLASS}
              style={{ ...SECTION_HEADING_STYLE, color: 'var(--m-on-band)', maxWidth: 560 }}
            >
              How we turn challenges
              <br />
              into clear growth plans
            </h2>
            <p
              className="max-w-xs text-sm leading-relaxed"
              style={{ color: 'rgba(var(--m-on-band-rgb),0.45)', lineHeight: 1.7 }}
            >
              A structured consulting process built on transparency, practical execution, and
              measurable outcomes.
            </p>
          </div>
        </motion.div>

        {/* Desktop — horizontal timeline */}
        <div className="hidden lg:block">
          {/* No plotted points here on purpose. They were tried and they landed
              behind the 80px icon boxes, which is where each step already sits;
              moving them off the steps would have made them mark nothing. The
              section carries its own numbering, so the instrument language has
              no reading to add — the line is merely given enough contrast to be
              seen. A mark that annotates nothing is how this motif gets spent. */}
          <div className="relative mb-12">
            {/* The one scroll-linked moment on the page, spent here because
                this content genuinely is a sequence — the line draws in the
                direction you read the five steps. A scaleX transform rather
                than a stroke-dashoffset: it is a div, not an SVG path, and
                transform is the only property that animates on the compositor.

                isInView already gates the step cards, so the line arrives with
                them rather than needing its own observer. */}
            <div
              className="absolute top-10 left-[10%] right-[10%]"
              style={{
                height: 1,
                background: 'rgba(var(--m-on-band-rgb),0.14)',
                transformOrigin: 'left center',
                transform: isInView || prefersReducedMotion ? 'scaleX(1)' : 'scaleX(0)',
                transition: prefersReducedMotion ? 'none' : 'transform 900ms var(--ease) 120ms',
              }}
            />
            <div className="grid grid-cols-5 gap-0">
              {steps?.map((step, i) => (
                <motion.div
                  key={step?.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: i * 0.12, duration: 0.5 }}
                  className="flex flex-col items-center text-center px-4"
                >
                  {/* Step node */}
                  <div className="relative mb-8">
                    <div
                      className="w-20 h-20 flex items-center justify-center relative z-10 transition-all duration-200"
                      style={{
                        background: 'rgba(var(--m-on-band-rgb),0.04)',
                        border: '1px solid rgba(var(--m-on-band-rgb),0.1)',
                      }}
                    >
                      <step.icon
                        className="w-7 h-7"
                        style={{ color: 'rgba(var(--m-on-band-rgb),0.6)' }}
                      />
                    </div>
                    {/* Step number badge */}
                    <div
                      className="absolute -top-2.5 -right-2.5 w-6 h-6 flex items-center justify-center z-20"
                      style={{
                        background: 'rgba(var(--m-on-band-rgb),0.1)',
                        border: '1px solid rgba(var(--m-on-band-rgb),0.15)',
                      }}
                    >
                      <span
                        className="font-mono text-[8px]"
                        style={{ color: 'rgba(var(--m-on-band-rgb),0.7)' }}
                      >
                        {step?.number}
                      </span>
                    </div>
                  </div>
                  <h3
                    className="font-bold uppercase mb-2"
                    style={{
                      fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                      fontSize: 12,
                      color: 'var(--m-on-band)',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {step?.title}
                  </h3>
                  <p
                    className="text-xs leading-relaxed mb-4"
                    style={{
                      color: 'rgba(var(--m-on-band-rgb),0.72)',
                      lineHeight: 1.65,
                      fontSize: 15,
                    }}
                  >
                    {step?.description}
                  </p>
                  <span
                    className="font-mono uppercase tracking-widest px-3 py-1.5"
                    style={{
                      fontSize: 11,
                      color: 'rgba(var(--m-on-band-rgb),0.35)',
                      border: '1px solid rgba(var(--m-on-band-rgb),0.1)',
                      letterSpacing: '0.12em',
                    }}
                  >
                    {step?.duration}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile — stacked cards */}
        <div
          className="lg:hidden space-y-px"
          style={{ background: 'rgba(var(--m-on-band-rgb),0.04)' }}
        >
          {steps?.map((step, i) => (
            <motion.div
              key={step?.id}
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className="flex gap-5 p-5"
              style={{
                background: 'rgba(var(--m-on-band-rgb),0.03)',
                borderLeft: '2px solid rgba(var(--m-on-band-rgb),0.08)',
              }}
            >
              <div className="flex flex-col items-center gap-2 flex-shrink-0">
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: 44,
                    height: 44,
                    border: '1px solid rgba(var(--m-on-band-rgb),0.1)',
                    background: 'rgba(var(--m-on-band-rgb),0.04)',
                  }}
                >
                  <step.icon
                    className="w-5 h-5"
                    style={{ color: 'rgba(var(--m-on-band-rgb),0.5)' }}
                  />
                </div>
                <span
                  className="font-mono text-[8px]"
                  style={{ color: 'rgba(var(--m-on-band-rgb),0.2)' }}
                >
                  {step?.number}
                </span>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1.5">
                  <h3
                    className="font-bold uppercase"
                    style={{
                      fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                      fontSize: 12,
                      color: 'var(--m-on-band)',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {step?.title}
                  </h3>
                  <span
                    className="font-mono uppercase tracking-widest px-2 py-0.5"
                    style={{
                      fontSize: 11,
                      color: 'rgba(var(--m-on-band-rgb),0.3)',
                      border: '1px solid rgba(var(--m-on-band-rgb),0.08)',
                      letterSpacing: '0.1em',
                    }}
                  >
                    {step?.duration}
                  </span>
                </div>
                <p
                  className="text-xs leading-relaxed"
                  style={{
                    color: 'rgba(var(--m-on-band-rgb),0.72)',
                    lineHeight: 1.65,
                    fontSize: 15,
                  }}
                >
                  {step?.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
